import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router";
import type { Activity, ActivityLink, TimelineItem } from "./api.js";
import { useApi } from "./context.js";
import { Badge, Empty, ErrorNote, formatDate, formValues, label } from "./ui.js";
import { Icon } from "./icons.js";

// Activities, tasks and notes (design v0.7, C12b): one timeline per record,
// with notes, calls, meetings and tasks, plus the Inbox's messages, stage
// changes, decisions, documents and agreement events. Quick add from the
// record (and anywhere, from the "+" in the bar or by keyboard: N for a note,
// T for a task, L to log a call); links default to the record you're on, and
// the related records it offers can be ticked too.

export type QuickKind = "note" | "call" | "task";
export type RecordLink = ActivityLink & { name: string };

const TYPE_LABEL: Record<string, string> = {
  note: "Note",
  call: "Call",
  meeting: "Meeting",
  email: "Email",
  sms: "Text",
  task: "Task",
  interview: "Interview",
};
const KIND_LABEL: Record<TimelineItem["kind"], string> = {
  activity: "Activity",
  message: "Message",
  stage: "Stage",
  decision: "Decision",
  document: "Document",
  agreement: "Agreement",
};
const KIND_PLURAL: Record<TimelineItem["kind"], string> = {
  activity: "Notes, calls and tasks",
  message: "Messages",
  stage: "Stage changes",
  decision: "Decisions",
  document: "Documents",
  agreement: "Agreement events",
};
const PAGE_OF: Record<string, string> = {
  candidate: "/candidates/",
  contact: "/contacts/",
  company: "/companies/",
  job: "/jobs/",
  placement: "/placements/",
  agreement: "/agreements/",
  decision: "/decisions/",
  thread: "/inbox/",
};
export const recordPath = (object: string, id: string) =>
  PAGE_OF[object] ? `${PAGE_OF[object]}${id}` : null;

// The record the page is about, for the "+" and the shortcuts.
type QuickAdd = {
  records: RecordLink[];
  setRecords: (r: RecordLink[]) => void;
  open: (kind: QuickKind) => void;
};
const QuickAddContext = createContext<QuickAdd | null>(null);

export function QuickAddProvider({ children }: { children: ReactNode }) {
  const [records, setRecords] = useState<RecordLink[]>([]);
  const [kind, setKind] = useState<QuickKind | null>(null);
  const open = useCallback((k: QuickKind) => setKind(k), []);
  // N, T and L, unless someone is typing.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName))) return;
      if (document.querySelector("dialog[open]")) return;
      const k = { n: "note", t: "task", l: "call" }[e.key.toLowerCase()] as QuickKind | undefined;
      if (k) {
        e.preventDefault();
        setKind(k);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const value = useMemo(() => ({ records, setRecords, open }), [records, open]);
  return (
    <QuickAddContext.Provider value={value}>
      {children}
      {kind ? <QuickAddDialog kind={kind} records={records} onClose={() => setKind(null)} /> : null}
    </QuickAddContext.Provider>
  );
}

export const useQuickAdd = () => useContext(QuickAddContext);

// A record page says what it's about, and which related records to offer.
export function useRecordLinks(records: RecordLink[]) {
  const q = useQuickAdd();
  const key = records.map((r) => `${r.object}:${r.id}`).join(",");
  useEffect(() => {
    if (!q) return;
    q.setRecords(records);
    return () => q.setRecords([]);
    // The records' identity is their key.
  }, [key]);
}

// The "+" in the bar.
export function QuickAddMenu() {
  const q = useQuickAdd();
  const [open, setOpen] = useState(false);
  if (!q) return null;
  const pick = (k: QuickKind) => {
    setOpen(false);
    q.open(k);
  };
  return (
    <span className="quick-add">
      <button
        type="button"
        className="secondary"
        aria-label="Add a note, call or task"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        + <span className="rail-label">New</span>
      </button>
      {open ? (
        <span role="menu" className="quick-add-menu">
          <button type="button" role="menuitem" className="link" onClick={() => pick("note")}>
            Note <kbd>N</kbd>
          </button>
          <button type="button" role="menuitem" className="link" onClick={() => pick("call")}>
            Log a call <kbd>L</kbd>
          </button>
          <button type="button" role="menuitem" className="link" onClick={() => pick("task")}>
            Task <kbd>T</kbd>
          </button>
        </span>
      ) : null}
    </span>
  );
}

// Plain text with blank lines as paragraphs; the API sanitizes and stores HTML.
function QuickAddDialog({
  kind,
  records,
  onClose,
}: {
  kind: QuickKind;
  records: RecordLink[];
  onClose: () => void;
}) {
  const api = useApi();
  const client = useQueryClient();
  const ref = useRef<HTMLDialogElement>(null);
  const owners = useQuery({ queryKey: ["task-owners"], queryFn: api.taskOwners });
  const outcomes = useQuery({
    queryKey: ["customization-registry"],
    queryFn: api.customizationRegistry,
  });
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
  }, []);
  const save = useMutation({
    mutationFn: (v: Record<string, string>) => {
      const links = records
        .filter((r, i) => i === 0 || v[`link_${r.object}_${r.id}`])
        .map((r, i) => ({ object: r.object, id: r.id, primary: i === 0 }));
      const base = {
        type: kind,
        subject: v.subject,
        ...(v.body_text ? { body_text: v.body_text } : {}),
        links,
        ...(v.visibility && v.visibility !== "team" ? { visibility: v.visibility } : {}),
        ...(v.visibility === "confidential"
          ? { visible_to_roles: ["admin", "account_manager"] }
          : {}),
      };
      if (kind === "call")
        return api.logActivity({
          ...base,
          direction: v.direction,
          ...(v.outcome ? { outcome: v.outcome } : {}),
          ...(v.duration_minutes ? { duration_minutes: Number(v.duration_minutes) } : {}),
        });
      if (kind === "task") {
        const [ownerKind, ownerId] = (v.owner ?? "").split(":");
        return api.logActivity({
          ...base,
          ...(ownerKind === "role"
            ? { owner_role_id: ownerId }
            : ownerId
              ? { owner_user_id: ownerId }
              : {}),
          ...(v.due_date ? { due_date: v.due_date } : {}),
          ...(v.due_time ? { due_time: v.due_time } : {}),
          priority: v.priority ?? "normal",
          ...(v.reminder_minutes ? { reminder_minutes: Number(v.reminder_minutes) } : {}),
          ...(v.repeat && v.repeat !== "none" ? { repeat: { every: v.repeat } } : {}),
        });
      }
      return api.logActivity(base);
    },
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: ["timeline"] }),
        client.invalidateQueries({ queryKey: ["tasks"] }),
        client.invalidateQueries({ queryKey: ["task-summary"] }),
        client.invalidateQueries({ queryKey: ["activities"] }),
        client.invalidateQueries({ queryKey: ["contact"] }),
      ]);
      onClose();
    },
  });
  const title = { note: "Add a note", call: "Log a call", task: "Add a task" }[kind];
  const needsRecord = kind !== "task" && !records.length;
  const callOutcomes =
    outcomes.data?.picklists
      ?.find((p) => p.key === "call_outcome")
      ?.values.filter((v) => v.status === "active") ?? [];
  return (
    <dialog ref={ref} className="quick-dialog" aria-label={title} onClose={onClose}>
      <form
        method="dialog"
        className="panel"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate(formValues(e.currentTarget));
        }}
      >
        <h2 className="wide">{title}</h2>
        {needsRecord ? (
          <p className="wide muted">
            Open a candidate, contact, company, job, placement or agreement to log a {kind} on it.
          </p>
        ) : (
          <>
            {records.length ? (
              <fieldset className="wide">
                <legend>On</legend>
                {records.map((r, i) => (
                  <label key={`${r.object}:${r.id}`} className="inline">
                    <input
                      type="checkbox"
                      name={`link_${r.object}_${r.id}`}
                      value="1"
                      defaultChecked={i === 0}
                      disabled={i === 0}
                    />{" "}
                    {r.name} <span className="muted">({label(r.object)})</span>
                  </label>
                ))}
              </fieldset>
            ) : null}
            <label className="wide">
              {kind === "task" ? "Title" : "Subject"}
              <input name="subject" required maxLength={kind === "task" ? 300 : 2000} autoFocus />
            </label>
            {kind === "call" ? (
              <>
                <label>
                  Direction
                  <select name="direction" defaultValue="outbound">
                    <option value="outbound">Outbound</option>
                    <option value="inbound">Inbound</option>
                  </select>
                </label>
                <label>
                  Outcome
                  <select name="outcome" defaultValue="">
                    <option value="">—</option>
                    {callOutcomes.map((o) => (
                      <option key={o.key} value={o.key}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Minutes <input name="duration_minutes" type="number" min="0" max="1440" />
                </label>
              </>
            ) : null}
            {kind === "task" ? (
              <>
                <label>
                  Owner
                  <select name="owner" defaultValue={owners.data ? `user:${owners.data.me}` : ""}>
                    {owners.data?.people.map((p) => (
                      <option key={p.id} value={`user:${p.id}`}>
                        {p.id === owners.data?.me ? `${p.name} (me)` : p.name}
                      </option>
                    ))}
                    {owners.data?.roles.length ? (
                      <optgroup label="Company roles">
                        {owners.data.roles.map((r) => (
                          <option key={r.id} value={`role:${r.id}`}>
                            {r.name}
                          </option>
                        ))}
                      </optgroup>
                    ) : null}
                  </select>
                </label>
                <label>
                  Due <input name="due_date" type="date" />
                </label>
                <label>
                  Time <input name="due_time" type="time" />
                </label>
                <label>
                  Priority
                  <select name="priority" defaultValue="normal">
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                  </select>
                </label>
                <label>
                  Reminder
                  <select name="reminder_minutes" defaultValue="">
                    <option value="">None</option>
                    <option value="15">15 minutes before</option>
                    <option value="60">1 hour before</option>
                    <option value="1440">1 day before</option>
                  </select>
                </label>
                <label>
                  Repeat
                  <select name="repeat" defaultValue="none">
                    <option value="none">Doesn't repeat</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </label>
              </>
            ) : null}
            <label className="wide">
              {kind === "task" ? "Description" : "Details"}{" "}
              <span className="muted">(@name tells a colleague)</span>
              <textarea name="body_text" rows={4} />
            </label>
            {kind !== "task" ? (
              <label>
                Who can see it
                <select name="visibility" defaultValue="team">
                  <option value="team">Everyone on staff</option>
                  <option value="private">Only me (and admins)</option>
                  <option value="confidential">Admins and account managers</option>
                </select>
              </label>
            ) : null}
          </>
        )}
        <ErrorNote error={save.error} />
        <div className="wide row">
          {needsRecord ? null : (
            <button type="submit" disabled={save.isPending}>
              Save
            </button>
          )}
          <button type="button" className="secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </dialog>
  );
}

// A task's due line: "Due Dec 1, 10:00" or "Overdue since …".
export function dueText(t: NonNullable<Activity["task"]>) {
  if (!t.due_date) return "No due date";
  const when = `${formatDate(t.due_date)}${t.due_time ? `, ${t.due_time}` : ""}`;
  if (t.status !== "open") return `Due ${when}`;
  return t.overdue ? `Overdue since ${when}` : `Due ${when}`;
}

export function TaskActions({ activity }: { activity: Activity }) {
  const api = useApi();
  const client = useQueryClient();
  const refresh = () =>
    Promise.all([
      client.invalidateQueries({ queryKey: ["timeline"] }),
      client.invalidateQueries({ queryKey: ["tasks"] }),
      client.invalidateQueries({ queryKey: ["task-summary"] }),
      client.invalidateQueries({ queryKey: ["inbox-items"] }),
    ]);
  const complete = useMutation({
    mutationFn: () => api.completeTask(activity.id),
    onSuccess: refresh,
  });
  const reopen = useMutation({ mutationFn: () => api.reopenTask(activity.id), onSuccess: refresh });
  const cancel = useMutation({ mutationFn: () => api.cancelTask(activity.id), onSuccess: refresh });
  const t = activity.task;
  if (!t) return null;
  return (
    <span className="row">
      {t.status === "open" ? (
        <>
          <button type="button" disabled={complete.isPending} onClick={() => complete.mutate()}>
            Done
          </button>
          <button
            type="button"
            className="secondary"
            disabled={cancel.isPending}
            onClick={() => cancel.mutate()}
          >
            Cancel task
          </button>
        </>
      ) : (
        <button
          type="button"
          className="secondary"
          disabled={reopen.isPending}
          onClick={() => reopen.mutate()}
        >
          Reopen
        </button>
      )}
      <ErrorNote error={complete.error ?? reopen.error ?? cancel.error} />
    </span>
  );
}

// One activity, as a card.
export function ActivityCard({
  activity: a,
  showLinks = true,
}: {
  activity: Activity;
  showLinks?: boolean;
}) {
  const api = useApi();
  const client = useQueryClient();
  const refresh = () =>
    Promise.all([
      client.invalidateQueries({ queryKey: ["timeline"] }),
      client.invalidateQueries({ queryKey: ["activities"] }),
      client.invalidateQueries({ queryKey: ["contact"] }),
    ]);
  const pin = useMutation({
    mutationFn: () => api.updateActivity(a.id, { pinned: !a.pinned }),
    onSuccess: refresh,
  });
  const archive = useMutation({ mutationFn: () => api.archiveActivity(a.id), onSuccess: refresh });
  const editable = a.editable_until != null && new Date(a.editable_until) > new Date();
  const remove = useMutation({ mutationFn: () => api.deleteActivity(a.id), onSuccess: refresh });
  const ai = a.actor_type === "ai_agent";
  return (
    <li className={a.pinned ? "card pinned timeline-item" : "card timeline-item"}>
      {/* A timeline item (design v0.9, V5): the type's tile, then the rest. */}
      <span className={ai ? "type-tile ai" : "type-tile"} aria-hidden="true">
        <Icon name={a.type} />
      </span>
      <div className="timeline-body">
        <div className="row">
          <span className={ai ? "badge ai" : "badge"}>{TYPE_LABEL[a.type] ?? label(a.type)}</span>
          {a.visibility !== "team" ? <Badge value={a.visibility} /> : null}
          {a.task ? <Badge value={a.task.status} /> : null}
          <strong>{a.subject}</strong>
          <span className="muted">
            {formatDate(a.occurred_at)} · {a.by ?? "—"}
          </span>
        </div>
        {a.task ? (
          <p className={a.task.overdue ? "danger" : "muted"}>
            {dueText(a.task)} · {a.task.owner_name ?? a.task.owner_role_name ?? "—"}
            {a.task.priority && a.task.priority !== "normal"
              ? ` · ${a.task.priority} priority`
              : ""}
            {a.task.repeat ? ` · repeats ${a.task.repeat.every}` : ""}
          </p>
        ) : null}
        {a.call ? (
          <p className="muted">
            {label(a.call.direction ?? "")}
            {a.call.outcome ? ` · ${label(a.call.outcome)}` : ""}
            {a.call.duration_minutes != null ? ` · ${a.call.duration_minutes} min` : ""}
          </p>
        ) : null}
        {/* Sanitized by the API: no scripts, no remote images. */}
        {a.body ? <div className="note-body" dangerouslySetInnerHTML={{ __html: a.body }} /> : null}
        {showLinks && a.links.length ? (
          <p className="muted">
            On:{" "}
            {a.links.map((l, i) => {
              const path = recordPath(l.object, l.id);
              const name = l.name ?? label(l.object);
              return (
                <span key={`${l.object}:${l.id}`}>
                  {i ? ", " : ""}
                  {path ? <Link to={path}>{name}</Link> : name}
                </span>
              );
            })}
          </p>
        ) : null}
        <div className="row">
          {a.task ? <TaskActions activity={a} /> : null}
          <button
            type="button"
            className="link"
            onClick={() => pin.mutate()}
            disabled={pin.isPending}
          >
            {a.pinned ? "Unpin" : "Pin"}
          </button>
          {editable && !a.task ? (
            <button
              type="button"
              className="link"
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
            >
              Delete
            </button>
          ) : (
            <button
              type="button"
              className="link"
              onClick={() => archive.mutate()}
              disabled={archive.isPending}
            >
              Archive
            </button>
          )}
        </div>
        <ErrorNote error={pin.error ?? archive.error ?? remove.error} />
      </div>
    </li>
  );
}

// A list of activities (a contact's, across every place they've held).
export function ActivityTimeline({
  activities,
}: {
  activities: Activity[];
  showContact?: boolean;
}) {
  if (!activities.length) return <Empty>Nothing logged yet.</Empty>;
  return (
    <ul className="cards">
      {activities.map((a) => (
        <ActivityCard key={a.id} activity={a} />
      ))}
    </ul>
  );
}

// The buttons above a timeline.
export function QuickAddButtons() {
  const q = useQuickAdd();
  if (!q) return null;
  return (
    <div className="row">
      <button type="button" className="secondary" onClick={() => q.open("note")}>
        Add note
      </button>
      <button type="button" className="secondary" onClick={() => q.open("call")}>
        Log a call
      </button>
      <button type="button" className="secondary" onClick={() => q.open("task")}>
        Add task
      </button>
    </div>
  );
}

// The buttons, for a page that shows its own list of activities.
export function RecordQuickAdd({ records }: { records: RecordLink[] }) {
  useRecordLinks(records);
  return <QuickAddButtons />;
}

// A record's timeline, with filters. `related` are the other records a new
// note or task may also go on.
export function RecordTimeline({
  object,
  id,
  name,
  related = [],
}: {
  object: string;
  id: string;
  name: string;
  related?: RecordLink[];
}) {
  const api = useApi();
  const [filters, setFilters] = useState<Record<string, string>>({});
  useRecordLinks([{ object, id, name, primary: true }, ...related]);
  const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
  const items = useQuery({
    queryKey: ["timeline", object, id, params],
    queryFn: () => api.timeline(object, id, params),
  });
  const set = (k: string, v: string) => setFilters((f) => ({ ...f, [k]: v }));
  return (
    <section aria-label="Timeline">
      <QuickAddButtons />
      <div className="row">
        <select
          aria-label="Show"
          value={filters.kind ?? ""}
          onChange={(e) => set("kind", e.target.value)}
        >
          <option value="">Everything</option>
          {Object.entries(KIND_PLURAL).map(([k, v]) => (
            <option key={k} value={k}>
              {v} only
            </option>
          ))}
        </select>
        <select
          aria-label="Type"
          value={filters.type ?? ""}
          onChange={(e) => set("type", e.target.value)}
        >
          <option value="">Any type</option>
          {Object.entries(TYPE_LABEL).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
        <label className="inline">
          From{" "}
          <input
            type="date"
            value={filters.from ?? ""}
            onChange={(e) => set("from", e.target.value)}
          />
        </label>
        <label className="inline">
          To{" "}
          <input type="date" value={filters.to ?? ""} onChange={(e) => set("to", e.target.value)} />
        </label>
      </div>
      <ErrorNote error={items.error} />
      {items.data && !items.data.data.length ? <Empty>Nothing here yet.</Empty> : null}
      <ul className="cards">
        {items.data?.data.map((i) =>
          i.activity ? (
            <ActivityCard key={`a:${i.id}`} activity={i.activity} />
          ) : (
            <li key={`${i.kind}:${i.id}`} className="card">
              <div className="row">
                <span className="badge">{KIND_LABEL[i.kind]}</span>
                {i.link && recordPath(i.link.object, i.link.id) ? (
                  <Link to={recordPath(i.link.object, i.link.id) ?? "/"}>{i.title}</Link>
                ) : (
                  <strong>{i.title}</strong>
                )}
                <span className="muted">
                  {formatDate(i.at)}
                  {i.by ? ` · ${i.by}` : ""}
                </span>
              </div>
              {i.detail ? <p className="muted">{i.detail}</p> : null}
            </li>
          ),
        )}
      </ul>
    </section>
  );
}
